/**
 * Security console API — admin policy + sessions + login events,
 * plus the self-service /account/security endpoints.
 */
import client from "./client";

export type Require2FATier =
  | "off"
  | "super_admins"
  | "admins_managers"
  | "all_users";

export interface SecurityPolicy {
  min_password_length: 8 | 10 | 12;
  require_uppercase: boolean;
  require_number: boolean;
  require_special: boolean;
  password_expiry_days: number | null; // null = never
  prevent_reuse_count: 0 | 3 | 5 | 10;
  max_failed_attempts: 0 | 3 | 5 | 10;
  lockout_duration_minutes: 0 | 15 | 30 | 60; // 0 = until admin unlocks
  session_timeout_minutes: 30 | 60 | 240 | 480 | 1440;
  require_2fa: Require2FATier;
}

export interface SessionRow {
  id: string;
  user_id?: string;
  user_email?: string;
  device_info: string | null;
  user_agent: string | null;
  ip_address: string | null;
  created_at: string;
  last_active_at: string | null;
  expires_at?: string;
  is_current?: boolean;
}

export type LoginEventType =
  | "login_success"
  | "login_failed"
  | "logout"
  | "password_changed"
  | "password_reset_requested"
  | "password_reset_completed"
  | "account_locked"
  | "account_unlocked"
  | "session_force_logout";

export interface LoginEventRow {
  id: string;
  user_id: string | null;
  user_email?: string | null;
  event_type: LoginEventType;
  reason: string | null;
  ip_address: string | null;
  user_agent: string | null;
  device_info: string | null;
  location_hint: string | null;
  created_at: string;
}

// ── Admin /settings/security/* ─────────────────────────────────────

export async function getSecurityPolicy(): Promise<SecurityPolicy> {
  const res = await client.get<SecurityPolicy>("/settings/security/policy");
  return res.data;
}

export async function updateSecurityPolicy(
  patch: Partial<SecurityPolicy>,
): Promise<SecurityPolicy> {
  const res = await client.put<SecurityPolicy>(
    "/settings/security/policy",
    patch,
  );
  return res.data;
}

export async function listOrgSessions(): Promise<SessionRow[]> {
  const res = await client.get<SessionRow[]>("/settings/security/sessions");
  return res.data;
}

export async function listUserSessions(userId: string): Promise<SessionRow[]> {
  const res = await client.get<SessionRow[]>(
    `/settings/security/users/${userId}/sessions`,
  );
  return res.data;
}

export async function adminForceLogoutSession(
  userId: string,
  sessionId: string,
): Promise<void> {
  await client.delete(
    `/settings/security/users/${userId}/sessions/${sessionId}`,
  );
}

export async function adminForceLogoutUser(userId: string): Promise<void> {
  await client.delete(`/settings/security/users/${userId}/sessions`);
}

export async function adminForceLogoutOrg(): Promise<{
  message: string;
  affected: number;
}> {
  const res = await client.post("/settings/security/force-logout-all", {
    confirmation: "CONFIRM",
  });
  return res.data;
}

export async function adminUnlockUser(userId: string): Promise<void> {
  await client.post(`/settings/security/users/${userId}/unlock`);
}

export interface LoginEventsParams {
  user_id?: string;
  event_type?: LoginEventType;
  days?: number;
}
export async function listLoginEvents(
  params: LoginEventsParams = {},
): Promise<LoginEventRow[]> {
  const res = await client.get<LoginEventRow[]>(
    "/settings/security/login-events",
    { params },
  );
  return res.data;
}

// ── Self-service /account/security/* ───────────────────────────────

export async function getMyPolicy(): Promise<SecurityPolicy> {
  const res = await client.get<SecurityPolicy>("/account/security/policy");
  return res.data;
}

export async function getMySessions(): Promise<SessionRow[]> {
  const res = await client.get<SessionRow[]>("/account/security/sessions");
  return res.data;
}

export async function logoutMyOtherSessions(): Promise<void> {
  await client.delete("/account/security/sessions/others");
}

export async function logoutMyAllSessions(): Promise<void> {
  await client.delete("/account/security/sessions");
}

export async function getMyLoginEvents(
  limit = 10,
): Promise<LoginEventRow[]> {
  const res = await client.get<LoginEventRow[]>(
    "/account/security/login-events",
    { params: { limit } },
  );
  return res.data;
}
